import type { IChatInfo } from "@/chats/chats-models";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import type { IPaidContentPopupProps } from "@/common/paid-content/paid-content-popup";
import type { IPopupConfig } from "@/models";
import {
  removeDuplicates,
  removeInArrayBy,
  replaceInArrayBy,
  sortByCreatedAt,
} from "@/utils/array";
import type { IGetRequest, IPostRequest, IRequest } from "@/utils/async";
import { ERequestStatus, isFetching, requestInit } from "@/utils/async";
import { defined, isArray } from "@/utils/variable-evaluation";
import { createReducer } from "@reduxjs/toolkit";
import { IMessengerActions } from "./messenger-actions";
import { nextMessagesWithErrorMessage } from "./messenger-helpers";
import type {
  IDestructibleMessage,
  IGetFromLastIdMessagesConfig,
  IMessageInfo,
  IUpdateMessagesReadConfig,
} from "./messenger-models";
import {
  CHAT_ACCOUNT_NO_LONGER_EXISTS_ERROR_MESSAGE,
  CHAT_BLOCKED_COUNTRY_ERROR_MESSAGE,
  CHAT_BLOCKED_ERROR_MESSAGE,
  CHAT_CLOSED_ERROR_MESSAGE,
  CHAT_FROZEN_ERROR_MESSAGE_MODEL,
  CHAT_FROZEN_ERROR_MESSAGE_USER,
  EMessageType,
  NO_CREDITS_ERROR_MESSAGE,
} from "./messenger-models";
// import { isSessionStorageSupport } from '../../utils/detectors';

export interface IMessengerModel {
  getChat: IGetRequest<any>;
  postCreateChat: IPostRequest<any, any>;
  chat: IChatInfo | null;
  getMessages: IGetRequest<any>;
  messages: IMessageInfo[] | null;
  nextMessagesUrl: string | null;
  getNextMessages: IGetRequest<any>;
  getMessageById: IGetRequest<any>;
  isNotesOpen: boolean;
  noteText: string;
  editingNoteId: string | null;
  // blockedChats: string[];
  // frozenChats: string[];
  // noCreditsChats: string[];
  isForceBlocked: boolean;
  isForceFrozen: boolean;
  isForceNoCredits: boolean;
  getFromLastIdMessages: IGetRequest<any>;
  patchBuyPaidContent: IPostRequest<any, any>;
  postSendMessage: IPostRequest<any, any>;
  paidContentConfig: Partial<IPaidContentPopupProps> | null;
  messengerPopup: IPopupConfig | null;
  postChatNote: IPostRequest<any, any>;
  patchChatNote: IPostRequest<any, any>;
  deleteChatNote: IRequest<any>;
  deleteMessage: IRequest<any>;
  patchHideMessage: IPostRequest<any, any>;
  getChatGallery: IGetRequest<any>;
  chatGallery: IMessageInfo[];
  nextChatGalleryUrl: string;
  getChatGalleryNext: IGetRequest<any>;
}

const INITIAL_STATE: IMessengerModel = {
  getChat: requestInit(),
  postCreateChat: requestInit(),
  chat: null,
  getMessages: requestInit(),
  messages: null,
  nextMessagesUrl: null,
  getNextMessages: requestInit(),
  getMessageById: requestInit(),
  isNotesOpen: false,
  noteText: "",
  editingNoteId: null,
  // blockedChats: [],
  // frozenChats: [],
  // noCreditsChats: [],
  isForceBlocked: false,
  isForceFrozen: false,
  isForceNoCredits: false,
  getFromLastIdMessages: requestInit(),
  patchBuyPaidContent: requestInit(),
  postSendMessage: requestInit(),
  paidContentConfig: null,
  messengerPopup: null,
  postChatNote: requestInit(),
  patchChatNote: requestInit(),
  deleteChatNote: requestInit(),
  deleteMessage: requestInit(),
  patchHideMessage: requestInit(),
  getChatGallery: requestInit(),
  chatGallery: [],
  nextChatGalleryUrl: "",
  getChatGalleryNext: requestInit(),
};

function updateMessageCollections(
  state: IMessengerModel,
  updatedMessage: IMessageInfo
) {
  const updateOne = (message: IMessageInfo) => {
    if (message._id === updatedMessage._id) {
      return { ...updatedMessage };
    }
    if (message.parent_message?._id === updatedMessage._id) {
      return {
        ...message,
        parent_message: { ...updatedMessage },
      };
    }
    return message;
  };

  return {
    messages: (state.messages || []).map(updateOne),
    chatGallery: (state.chatGallery || []).map(updateOne),
  };
}

// let clientSideMessages: IClientSideMessage[] = isSessionStorageSupport ? Object.keys(sessionStorage).reduce((obj, key) => {
//   if (key.startsWith('client-side-message-')) {
//     obj.push(JSON.parse(sessionStorage.getItem(key)));
//   }
//   return obj;
// }, []) : [];
const clientSideMessages: IMessageInfo[] = [];

/** Reducer */
export const messengerReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IMessengerActions.initGetChat,
      (state, action: any): IMessengerModel => {
        // abort previous request if any
        if (isFetching(state.getChat) && state.getChat.abortController) {
          state.getChat.abortController.abort();
        }

        const abortController = action.payload
          .abortController as AbortController;

        return {
          ...state,
          getChat: {
            status: ERequestStatus.Fetching,
            abortController,
          },
          chat: action.payload.cachedChat || null, // optimization
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChat,
      (state, action: any): IMessengerModel => {
        const chat: IChatInfo = action.payload;

        const activeOtherUserId = window.location.pathname.split("/")[2];
        if (chat.other_user._id !== activeOtherUserId) {
          // don't do anything if active chat is already changed
          return state;
        }

        // reconsider local blocks
        // const isBlockedChat = state.blockedChats.indexOf(chat._id) !== -1;
        // const isFrozenChat = state.frozenChats.indexOf(chat._id) !== -1;
        // const isNoCreditsChat = state.noCreditsChats.indexOf(chat._id) !== -1;

        return {
          ...state,
          getChat: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chat,
          // blockedChats: isBlockedChat ? state.blockedChats.filter((chatId) => chatId !== chat._id) : state.blockedChats,
          isForceBlocked: false,
          // frozenChats: isFrozenChat ? state.frozenChats.filter((chatId) => chatId !== chat._id) : state.frozenChats,
          isForceFrozen: false,
          // noCreditsChats: isNoCreditsChat
          //   ? state.noCreditsChats.filter((chatId) => chatId !== chat._id)
          //   : state.noCreditsChats,
          isForceNoCredits: false,
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetChat,
      (state, action: any): IMessengerModel => {
        if (action.payload && action.payload.name === "AbortError") {
          // abort happens if there is another fetch
          // therefore we can continue with status as Fetching here
          return state;
        }

        return {
          ...state,
          getChat: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
          chat: null,
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.resetGetChat,
      (state, action: any): IMessengerModel => {
        if (state.getChat.abortController) {
          state.getChat.abortController.abort();
        }
        return {
          ...state,
          getChat: requestInit(),
          chat: null,
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.initGetChatReload,
      (state, action: any): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.successGetChatReload,
      (state, action: any): IMessengerModel => {
        const chat: IChatInfo = action.payload;

        // reconsider local blocks
        // const isBlockedChat = state.blockedChats.indexOf(chat._id) !== -1;
        // const isFrozenChat = state.frozenChats.indexOf(chat._id) !== -1;
        // const isNoCreditsChat = state.noCreditsChats.indexOf(chat._id) !== -1;

        return {
          ...state,
          // getChat: {
          //   responseData: action.payload,
          //   status: ERequestStatus.Fetched

          chat,
          // blockedChats: isBlockedChat ? state.blockedChats.filter((chatId) => chatId !== chat._id) : state.blockedChats,
          // isForceBlocked: false,
          // frozenChats: isFrozenChat ? state.frozenChats.filter((chatId) => chatId !== chat._id) : state.frozenChats,
          // isForceFrozen: false,
          // noCreditsChats: isNoCreditsChat
          //   ? state.noCreditsChats.filter((chatId) => chatId !== chat._id)
          //   : state.noCreditsChats,
          // isForceNoCredits: false,
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetChatReload,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          // getChat: {
          //   error: action.payload,
          //   status: ERequestStatus.Error
          // }
        };
      }
    )
    .addCase(
      IMessengerActions.initPostCreateChat,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postCreateChat: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successPostCreateChat,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postCreateChat: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chat: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePostCreateChat,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postCreateChat: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.updateChatById,
      (state, action: any): IMessengerModel => {
        const { chat_id, newValues } = action.payload;

        if (!state.chat || chat_id !== state.chat._id) {
          return state;
        }

        return {
          ...state,
          chat: { ...state.chat, ...newValues },
        };
      }
    )
    .addCase(
      IMessengerActions.initGetMessages,
      (state, action: any): IMessengerModel => {
        // abort previous request if any
        if (
          isFetching(state.getMessages) &&
          state.getMessages.abortController
        ) {
          state.getMessages.abortController.abort();
        }

        // const chatId = action.payload.chatId as string;
        // const otherUserId = action.payload.otherUserId as string;
        const abortController = action.payload
          .abortController as AbortController;

        return {
          ...state,
          getMessages: {
            status: ERequestStatus.Fetching,
            abortController,
          },
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.successGetMessages,
      (state, action: any): IMessengerModel => {
        // const chatId = action.payload.chatId as string;
        const otherUserId = action.payload.otherUserId as string;
        const res = action.payload.res;

        const activeOtherUserId = window.location.pathname.split("/")[2];
        if (otherUserId !== activeOtherUserId) {
          // don't do anything if active chat is already changed
          return state;
        }

        const newMessages = res.data;

        let messages = newMessages || [];
        const currentChatClientSideMessages = clientSideMessages.filter(
          (m) => state.chat && m.chat_id === state.chat._id
        );

        // add client side messages if any
        messages = [...messages, ...currentChatClientSideMessages];
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          getMessages: {
            responseData: res.data,
            status: ERequestStatus.Fetched,
          },
          nextMessagesUrl: res.data.length > 0 ? res.next : "",
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetMessages,
      (state, action: any): IMessengerModel => {
        if (action.payload && action.payload.name === "AbortError") {
          // abort happens if there is another fetch
          // therefore we can continue with status as Fetching here
          return state;
        }

        return {
          ...state,
          getMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetGetMessages,
      (state, action: any): IMessengerModel => {
        if (state.getMessages.abortController) {
          state.getMessages.abortController.abort();
        }
        return {
          ...state,
          getMessages: requestInit(),
          messages: null,
        };
      }
    )
    .addCase(
      IMessengerActions.initGetNextMessages,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getNextMessages: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetNextMessages,
      (state, action: any): IMessengerModel => {
        const newMessages = action.payload.data;

        let messages = [...newMessages, ...(state.messages || [])];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          getNextMessages: {
            responseData: action.payload.data,
            status: ERequestStatus.Fetched,
          },
          nextMessagesUrl: action.payload.next,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetNextMessages,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getNextMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessagesRead,
      (state, action: any): IMessengerModel => {
        const config: IUpdateMessagesReadConfig = action.payload.config;
        const { timestampString, messageIds, destructibles } = config;
        let messageList = state.messages;
        if (messageList) {
          messageList.forEach((message) => {
            messageIds.forEach((id: string) => {
              if (id === message._id) {
                messageList = replaceInArrayBy("_id", messageList!, id, {
                  ...message,
                  is_read: true,
                  read_at: timestampString,
                });
              }
            });
            if (
              defined(destructibles) &&
              isArray(destructibles) &&
              destructibles.length > 0
            ) {
              destructibles.forEach((destructible: IDestructibleMessage) => {
                if (destructible.message_id === message._id) {
                  messageList = replaceInArrayBy(
                    "_id",
                    messageList!,
                    destructible.message_id,
                    {
                      ...message,
                      destruct_at: destructible.destruct_at,
                    }
                  );
                }
              });
            }
          });
        }
        return { ...state, messages: messageList };
      }
    )
    .addCase(
      IMessengerActions.updateMessageRevealMedia,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) =>
            message._id === action.payload.messageId
              ? {
                  ...message,
                  isRevealed: true,
                }
              : message
          );
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsPaid,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.messageId) {
              return {
                ...message,
                is_paid: true,
                destruct_at: action.payload.destructAt,
              };
            }
            if (message.parent_message?._id === action.payload.messageId) {
              return {
                ...message,
                parent_message: {
                  ...(message.parent_message as IMessageInfo),
                  is_paid: true,
                  destruct_at: action.payload.destructAt,
                },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsHidden,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.hiddenMessage._id) {
              return { ...action.payload.hiddenMessage };
            }
            if (
              message.parent_message?._id === action.payload.hiddenMessage._id
            ) {
              return {
                ...message,
                parent_message: { ...action.payload.hiddenMessage },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsDeleted,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.deletedMessage._id) {
              return { ...action.payload.deletedMessage };
            }
            if (
              message.parent_message?._id === action.payload.deletedMessage._id
            ) {
              return {
                ...message,
                parent_message: { ...action.payload.deletedMessage },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessage,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message) => {
            if (message._id === action.payload.message._id) {
              return { ...action.payload.message };
            }
            if (message.parent_message?._id === action.payload.message._id) {
              return {
                ...message,
                parent_message: { ...action.payload.message },
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageAsExpired,
      (state, action: any): IMessengerModel => {
        let messageList = state.messages;
        if (messageList) {
          messageList = state.messages!.map((message): IMessageInfo => {
            if (message._id === action.payload.messageId) {
              // Replace message with expired placeholder, preserving existing state and price info
              return {
                ...message,
                text: "This content has expired",
                type: EMessageType.Text,
                is_deleted: true,
                // Remove only media and self-destruct properties
                image: undefined,
                video: undefined,
                self_destruct: undefined,
                self_destruct_hours: undefined,
                destruct_at: undefined,
              } as IMessageInfo;
            }
            if (message.parent_message?._id === action.payload.messageId) {
              // Also replace in parent message if needed
              return {
                ...message,
                parent_message: {
                  ...message.parent_message,
                  text: "This content has expired",
                  type: EMessageType.Text,
                  is_deleted: true,
                  // Remove only media and self-destruct properties
                  image: undefined,
                  video: undefined,
                  self_destruct: undefined,
                  self_destruct_hours: undefined,
                  destruct_at: undefined,
                } as IMessageInfo,
              };
            }
            return message;
          });
        }
        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.removeClientSideMessage,
      (state, action: any): IMessengerModel => {
        const messages = removeInArrayBy(
          "__clientSideSendId",
          state.messages || [],
          action.payload.__clientSideSendId
        );

        // remove from global clientSideMessages too
        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        if (index !== -1) {
          clientSideMessages.splice(index, 1);
        }
        // sessionStorage.removeItem(`client-side-message-${action.payload.__clientSideSendId}`);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.resetPostSendMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postSendMessage: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.setMessengerPopup,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          messengerPopup: action.payload,
        };
      }
    )
    // [IMessengerActions.Type.SET_CURRENT_ID]: (state, action: any) => {

    // [IMessengerActions.Type.SET_FIRST_LOAD]: (state, action: any) => {

    .addCase(
      IMessengerActions.togglePaidContent,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          paidContentConfig: action.payload.paidContentConfig,
        };
      }
    )
    .addCase(
      IMessengerActions.initGetMessageById,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getMessageById: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetMessageById,
      (state, action: any): IMessengerModel => {
        const updatedMessage: IMessageInfo = action.payload;

        return {
          ...state,
          getMessageById: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages: (state.messages || []).map((message) =>
            message._id === updatedMessage._id ? updatedMessage : message
          ),
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetMessageById,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getMessageById: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.initGetFromLastIdMessages,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getFromLastIdMessages: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetFromLastIdMessages,
      (state, action: any): IMessengerModel => {
        const {
          res,
          config,
        }: { res: any; config: IGetFromLastIdMessagesConfig } = action.payload;
        const messagesFromLastId = res.data;

        if (!state.chat || config.chat_id !== state.chat._id) {
          return state;
        }

        let messages = [
          ...(state.messages || []).filter(
            (message) => !defined(message.clientSideReceiveId)
          ),
          ...messagesFromLastId,
        ];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          getFromLastIdMessages: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetFromLastIdMessages,
      (state, action: any): IMessengerModel => {
        // const { err, config }: { err: any, config: IGetFromLastIdMessagesConfig } = action.payload;

        // const messages = nextMessagesWithErrorMessage(
        //   state.messages || [],
        //   { ...config, error: err },
        //   'clientSideReceiveId'
        // );

        return {
          ...state,
          getFromLastIdMessages: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
          messages: state.messages,
        };
      }
    )
    .addCase(
      IMessengerActions.addClientSideMessageReceived,
      (state, action: any): IMessengerModel => {
        const clientSideMessage: IMessageInfo =
          action.payload.clientSideMessage;

        if (
          !state.chat ||
          clientSideMessage.sender_id !== state.chat.other_user._id
        ) {
          return state;
        }

        let messages = state.messages || [];

        messages = [...messages, clientSideMessage];
        messages = removeDuplicates(messages, "_id", true);
        messages = messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.initPostSendMessage,
      (state, action: any): IMessengerModel => {
        const newMessage: IMessageInfo = {
          ...action.payload.clientSideMessage,
        }; // this can be both media or text
        delete newMessage.error; // remove error on init (resend)

        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === newMessage.__clientSideSendId
        );
        if (index !== -1) {
          // resend?
          clientSideMessages.splice(index, 1, newMessage); // replace
        } else {
          clientSideMessages.push(newMessage);
        }
        // sessionStorage.setItem(`client-side-message-${newMessage.__clientSideSendId}`, JSON.stringify(newMessage));

        if (
          !state.chat ||
          newMessage.chat_id !== state.chat._id ||
          state.getMessages.status === ERequestStatus.Fetching
        ) {
          return state;
        }

        let messages = state.messages || [];

        let isAlreadyInTheList = false; // for resend it will be already there
        messages = messages.map((message: IMessageInfo) => {
          if (message.__clientSideSendId === newMessage.__clientSideSendId) {
            isAlreadyInTheList = true;
            return newMessage;
          }
          return message;
        });
        if (!isAlreadyInTheList) {
          messages = [...messages, newMessage];
        }
        messages.sort(sortByCreatedAt);

        return {
          ...state,
          postSendMessage: {
            __clientSideSendId: action.payload.__clientSideSendId,
            status: ERequestStatus.Fetching,
          } as any,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.updateSendMessageProgress,
      (state, action: any): IMessengerModel => {
        // update in global clientSideMessages too with new progress value
        const oldMessage = clientSideMessages.find(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        let newMessage: IMessageInfo | null = null;
        if (oldMessage !== null) {
          newMessage = {
            ...oldMessage!,
            uploadProgress: action.payload.progress,
            loaded: action.payload.loaded,
            total: action.payload.total,
          };
          const index = clientSideMessages.findIndex(
            (m) => m.__clientSideSendId === action.payload.__clientSideSendId
          );
          clientSideMessages.splice(index, 1, newMessage);
        }

        if (
          !state.chat ||
          (newMessage && newMessage.chat_id !== state.chat._id) ||
          state.getMessages.status === ERequestStatus.Fetching
        ) {
          return state;
        }

        let messages = state.messages || [];

        let isAlreadyInTheList = false;
        messages = messages.map((message) => {
          if (
            newMessage &&
            message.__clientSideSendId === newMessage.__clientSideSendId
          ) {
            isAlreadyInTheList = true;
            return newMessage;
          }
          return message;
        });
        if (!isAlreadyInTheList) {
          // to handle cases like exit chat and return back
          // if this message is from this chat but is not in the list
          // we have to add it
          messages = [...messages!, newMessage!];
        }
        messages.sort(sortByCreatedAt);

        return {
          ...state,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.successPostSendMessage,
      (state, action: any): IMessengerModel => {
        // remove from global clientSideMessages too
        const index = clientSideMessages.findIndex(
          (m) => m.__clientSideSendId === action.payload.__clientSideSendId
        );
        if (index !== -1) {
          clientSideMessages.splice(index, 1);
        }
        // sessionStorage.removeItem(`client-side-message-${__clientSideSendId}`);

        const newMessage: IMessageInfo = action.payload.message;

        if (!state.chat || newMessage.chat_id !== state.chat._id) {
          return state;
        }

        let messages = state.messages || [];
        messages = replaceInArrayBy(
          "__clientSideSendId",
          messages,
          action.payload.__clientSideSendId,
          newMessage
        );
        messages = removeDuplicates(messages, "_id");

        return {
          ...state,
          postSendMessage: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePostSendMessage,
      (state, action: any): IMessengerModel => {
        const newMessage: IMessageInfo = action.payload;

        // let blockedChats = state.blockedChats;
        let isForceBlocked = state.isForceBlocked;
        if (
          action.payload.error.message === CHAT_CLOSED_ERROR_MESSAGE ||
          action.payload.error.message === CHAT_BLOCKED_ERROR_MESSAGE ||
          action.payload.error.message === CHAT_BLOCKED_COUNTRY_ERROR_MESSAGE ||
          action.payload.error.message ===
            CHAT_ACCOUNT_NO_LONGER_EXISTS_ERROR_MESSAGE
        ) {
          // blockedChats = addToArrayIfNotThere(
          //   blockedChats,
          //   action.payload.chat_id
          // );
          isForceBlocked = true;
        }

        // let frozenChats = state.frozenChats;
        let isForceFrozen = state.isForceFrozen;
        if (
          action.payload.error.message === CHAT_FROZEN_ERROR_MESSAGE_MODEL ||
          action.payload.error.message === CHAT_FROZEN_ERROR_MESSAGE_USER
        ) {
          // frozenChats = addToArrayIfNotThere(frozenChats, action.payload.chat_id);
          isForceFrozen = true;
        }

        // let noCreditsChats = state.noCreditsChats;
        let isForceNoCredits = state.isForceNoCredits;
        if (action.payload.error.message === NO_CREDITS_ERROR_MESSAGE) {
          // noCreditsChats = addToArrayIfNotThere(
          //   noCreditsChats,
          //   action.payload.chat_id
          // );
          isForceNoCredits = true;
        }

        let messages = state.messages || [];
        if (
          state.chat &&
          state.chat.other_user._id === newMessage.recipient_id
        ) {
          messages = nextMessagesWithErrorMessage(
            messages,
            action.payload,
            "__clientSideSendId"
          );
        }

        return {
          ...state,
          postSendMessage: {
            error: action.payload.error,
            status: ERequestStatus.Error,
          },
          // blockedChats,
          isForceBlocked,
          // frozenChats,
          isForceFrozen,
          // noCreditsChats,
          isForceNoCredits,
          messages,
        };
      }
    )
    .addCase(
      IMessengerActions.initPatchBuyPaidContent,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchBuyPaidContent: {
            requestData: action.payload.messageId,
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchBuyPaidContent,
      (state, action: any): IMessengerModel => {
        const updatedMessage: IMessageInfo = action.payload;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          updatedMessage
        );
        return {
          ...state,
          patchBuyPaidContent: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePatchBuyPaidContent,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchBuyPaidContent: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetPatchBuyPaidContent,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchBuyPaidContent: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.updateMessageUnlockByPayingUsd,
      (state, action: any): IMessengerModel => {
        const unlockedMessage = action.payload.message;
        const { messages, chatGallery } = updateMessageCollections(
          state,
          unlockedMessage
        );
        return {
          ...state,
          messages,
          chatGallery,
        };
      }
    )
    .addCase(
      IMessengerActions.initPostChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: {
            status: ERequestStatus.Fetching,
          },
          patchChatNote: requestInit(), // reset
          deleteChatNote: requestInit(), // reset
        };
      }
    )
    .addCase(
      IMessengerActions.successPostChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chat: state.chat
            ? {
                ...state.chat,
                notes: [...state.chat.notes, action.payload],
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePostChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetPostChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.initPatchChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: requestInit(), // reset
          patchChatNote: {
            status: ERequestStatus.Fetching,
          },
          deleteChatNote: requestInit(), // reset
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchChatNote: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chat: state.chat
            ? {
                ...state.chat,
                notes: state.chat.notes.map((note) =>
                  note._id === action.payload._id ? action.payload : note
                ),
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePatchChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchChatNote: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetPatchChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchChatNote: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.initDeleteChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          postChatNote: requestInit(), // reset
          patchChatNote: requestInit(), // reset
          deleteChatNote: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successDeleteChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteChatNote: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chat: state.chat
            ? {
                ...state.chat,
                notes: state.chat.notes.filter(
                  (note) => note._id !== action.payload.id
                ),
              }
            : state.chat,
        };
      }
    )
    .addCase(
      IMessengerActions.failureDeleteChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteChatNote: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetDeleteChatNote,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteChatNote: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.setNoteText,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          noteText: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.setEditingNoteId,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          editingNoteId: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.setNotesOpen,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          isNotesOpen: action.payload,
        };
      }
    )
    .addCase(
      IMessengerActions.initDeleteMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteMessage: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successDeleteMessage,
      (state, action: any): IMessengerModel => {
        const deletedMessage = action.payload.response;
        return {
          ...state,
          deleteMessage: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages: (state.messages || []).map((message) => {
            if (message._id === deletedMessage._id) {
              return { ...deletedMessage };
            }
            if (message.parent_message?._id === deletedMessage._id) {
              return {
                ...message,
                parent_message: { ...deletedMessage },
              };
            }
            return message;
          }),
        };
      }
    )
    .addCase(
      IMessengerActions.failureDeleteMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteMessage: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetDeleteMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          deleteMessage: requestInit(),
        };
      }
    )
    .addCase(
      IMessengerActions.initPatchHideMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchHideMessage: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successPatchHideMessage,
      (state, action: any): IMessengerModel => {
        const hiddenMessage = action.payload.response;
        return {
          ...state,
          patchHideMessage: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          messages: (state.messages || []).map((message) => {
            if (message._id === hiddenMessage._id) {
              return { ...hiddenMessage };
            }
            if (message.parent_message?._id === hiddenMessage._id) {
              return {
                ...message,
                parent_message: { ...hiddenMessage },
              };
            }
            return message;
          }),
        };
      }
    )
    .addCase(
      IMessengerActions.failurePatchHideMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchHideMessage: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.resetPatchHideMessage,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          patchHideMessage: requestInit(),
        };
      }
    )
    .addCase(
      IHeaderModalActions.successPatchUpdateChatUnhide,
      (state, action: any): IMessengerModel => {
        const chatId = action.payload.chatId;
        if (!chatId || !state.chat || state.chat._id !== chatId) {
          return state;
        }

        return {
          ...state,
          chat: {
            ...state.chat,
            is_hidden: false,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.initPostMessageReaction,
      (state, action: any): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.successPostMessageReaction,
      (state, action: any): IMessengerModel => {
        const { messageId, response } = action.payload;
        let messageList = state.messages;

        if (messageList && response) {
          messageList = state.messages!.map((message) => {
            if (message._id === messageId) {
              return { ...response };
            }
            // Also update parent message if it matches
            if (message.parent_message?._id === messageId) {
              return {
                ...message,
                parent_message: { ...response },
              };
            }
            return message;
          });
        }

        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.failurePostMessageReaction,
      (state, action: any): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.initDeleteMessageReaction,
      (state, action: any): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.successDeleteMessageReaction,
      (state, action: any): IMessengerModel => {
        const { messageId, response } = action.payload;
        let messageList = state.messages;

        if (messageList && response) {
          messageList = state.messages!.map((message) => {
            if (message._id === messageId) {
              return { ...response };
            }
            // Also update parent message if it matches
            if (message.parent_message?._id === messageId) {
              return {
                ...message,
                parent_message: { ...response },
              };
            }
            return message;
          });
        }

        return {
          ...state,
          messages: messageList,
        };
      }
    )
    .addCase(
      IMessengerActions.failureDeleteMessageReaction,
      (state, action: any): IMessengerModel => {
        return state;
      }
    )
    .addCase(
      IMessengerActions.initGetChatGallery,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGallery: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChatGallery,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGallery: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chatGallery: action.payload.data || [],
          nextChatGalleryUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetChatGallery,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGallery: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.initGetChatGalleryNext,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGalleryNext: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.successGetChatGalleryNext,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGalleryNext: {
            responseData: action.payload,
            status: ERequestStatus.Fetched,
          },
          chatGallery: [...state.chatGallery, ...(action.payload.data || [])],
          nextChatGalleryUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IMessengerActions.failureGetChatGalleryNext,
      (state, action: any): IMessengerModel => {
        return {
          ...state,
          getChatGalleryNext: {
            error: action.payload,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IMessengerActions.removeExpiredFromChatGallery,
      (state, action: any): IMessengerModel => {
        const chatGallery = state.chatGallery.filter(
          (message) => message._id !== action.payload.messageId
        );
        return {
          ...state,
          chatGallery,
        };
      }
    );
});
